<template>
  <div class="order-details-dialog">
    <el-dialog
        v-loading="loading"
        :fullscreen="true"
        :close-on-press-escape="false"
        :show-close="false"
        :close-on-click-modal="false"
        :visible.sync="visible">
      <div
          v-if="!isEmbedded"
          id="order-details-title"
          class="order-details-title-bar">
        <div class="order-number">
          Order #{{ order.order.displayable_order_id || order.order.order_id }}
        </div>
        <button
            type="button"
            class="el-dialog__headerbtn"
            aria-label="Close"
            @click="$emit('update:visible', false)">
          <i class="el-dialog__close el-icon el-icon-close"></i>
        </button>
      </div>
      <div
          v-if="!loading && error"
          class="order-details-error">
        <div class="order-details-error-content">
          <i
              class="el-icon-warning-outline"
              style="font-size: 48px; color: #F56C6C; margin-bottom: 16px;" />
          <div style="font-size: 16px; font-weight: 500; margin-bottom: 8px;">
            Unable to load order details
          </div>
          <div style="color: #6e6e6e;">
            The order may not exist or could not be retrieved. {{ error.message }}
          </div>
          <div
              v-if="error.status"
              style="color: #909399; margin-top: 4px; font-size: 12px;">
            Error code: {{ error.status }}
          </div>
        </div>
      </div>
      <div
          v-if="!loading && !error"
          id="order-details-body"
          :class="['order-details-body', { 'embedded': isEmbedded }]">
        <div class="order-details-timeline">
          <OrderTimeline
              :order="order"
              @return-id="getReturnID"
              @return-created="getValue" />
        </div>
        <div style="border: 1px solid #DEE5EC;"></div>
        <div
            id="order-details-data"
            class="order-details-data">
          <div class="flex flex-row items-center">
            <div class="order-number">
              Order #{{ order.order.displayable_order_id || order.order.order_id }}
            </div>
            <OrderStatus :order-status="order.order.state_name" />
          </div>
          <div
              class="flex flex-row items-center"
              style="margin: 16px 0">
            <div style="font-weight: 500">
              Date Placed:
            </div>
            <div style="margin-left: 4px">
              <time-zone-formatter
                  output-format="MMM DD, YYYY"
                  :current-time="order.order.created_at" />
            </div>
            <div style="font-weight: 500; margin-left: 26px">
              Email:
            </div>
            <div style="margin-left: 4px">
              {{ order.customer.customer_email }}
            </div>
          </div>
          <div
              id="order-details-items">
            <el-alert
                v-if="containsInstallationAsin"
                title="Installation Service — Do Not Refund or Return"
                type="warning"
                :closable="false"
                show-icon
                style="margin-bottom: 16px">
              <span>
                The ASIN <strong>IG-5X-601-PIN</strong> is an <strong>Installation</strong> service provided by a third party.
                Do not process refunds or returns on this item.
                Please check your resources for guidance on supporting installations, or reach out to your leadership for assistance.
              </span>
            </el-alert>
            <order-details-item-view
                v-for="(item, idx) in order.order_items"
                :key="idx"
                :title="item.title"
                :variant-title="item.variant_title"
                :sku="item.sku"
                :status="item.status"
                :pre-tax-price="item.pre_tax_price"
                :images="item.images"
                :items="item.items"
                :sub-item="item.sub_items"
                :quantity="item.quantity"
                style="margin-bottom: 8px" />
          </div>
          <div
              class="order-details-price-totals"
              style="margin-right: 25px">
            <div class="order-details-price-item flex flex-row">
              <div class="flex-1">
                Subtotal
              </div>
              <div>{{ formattedAmount(order.order.total_line_items_price.amount, order.order.total_line_items_price.currency_code) }}</div>
            </div>
            <div
                v-if="order.order.total_discounts.amount"
                class="order-details-price-item flex flex-row">
              <div class="flex-1 flex">
                <div>
                  Discount
                </div>
                <div
                    v-for="discount_code in order.order.discount_codes"
                    :key="discount_code.code"
                    class="discount-code">
                  {{ discount_code.code }} – {{ formattedDiscountCode(discount_code.type, discount_code.amount) }}
                </div>
              </div>
              <div>{{ formattedAmount(-order.order.total_discounts.amount, order.order.total_discounts.currency_code) }}</div>
            </div>
            <div class="order-details-price-item flex flex-row">
              <div class="flex-1 flex">
                <div>
                  Tax
                </div>
              </div>
              <div>{{ formattedAmount(order.order.total_tax.amount, order.order.total_tax.currency_code) }}</div>
            </div>
            <div style="border: 1px solid #DEE5EC;"></div>
            <div
                class="order-details-price-item flex flex-row"
                style="font-weight: 500">
              <div class="flex-1 flex">
                <div>
                  Total
                </div>
              </div>
              <div>{{ formattedAmount(order.order.total_price.amount, order.order.total_price.currency_code) }}</div>
            </div>
          </div>
          <div
              class="order-details-price-item flex flex-row"
              style="font-weight: 500; margin-top: 40px">
            <div class="flex-1 flex">
              <div>
                Shipment
              </div>
            </div>
          </div>
          <div
              id="order-details-items">
            <shipping-details-item-view
                v-for="(item, idx) in shipmentsToShow"
                :key="idx"
                :shipping-data="[item]"
                v-bind="item"
                style="margin-bottom: 8px" />
          </div>
          <div
              v-if="shipmentsToShow.length === 0"
              class="item-details">
            <shipment-details-available-soon />
          </div>
          <div
              v-else-if="order.order_items.length > totalItemsShipped"
              class="item-details">
            <shipment-details-not-available :total-shipments="shipmentsToShow.length" />
          </div>
          <div
              class="order-details-price-item flex flex-row"
              style="font-weight: 500">
            <div class="flex-1 flex">
              <div>
                Customer Info
              </div>
            </div>
          </div>
          <div
              id="order-details-items">
            <customer-details-item-view
                :billing-address="order.order.billing_address"
                :customer-data="order.customer"
                :shipping-address="order.order.shipping_address"
                style="margin-bottom: 8px" />
          </div>
        </div>
      </div>
      <div
          v-if="!error"
          id="order-details-footer"
          class="order-details-footer">
        <el-dropdown
            trigger="click"
            @command="handleAction">
          <el-button
              plain
              class="choose_action_button">
            Choose Action <i class="el-icon-arrow-down el-icon--right"></i>
          </el-button>
          <el-dropdown-menu
              slot="dropdown"
              style="max-height: unset !important; overflow: unset !important;">
            <el-tooltip
                :disabled="!checkRefundable && !installationRefundBlocked"
                class="box-item"
                :content="installationRefundBlocked ? 'Refunds and returns for installation services require leadership approval' : 'No items in this order are currently eligible for return'"
                placement="left">
              <span>
                <el-dropdown-item
                    v-if="!showReturnRequest"
                    :disabled="checkRefundable || installationRefundBlocked"
                    class="choose_action_item"
                    command="return">
                  Return Item(s)
                </el-dropdown-item>
              </span>
            </el-tooltip>
            <el-tooltip
                :disabled="(hasLisbonRefundPermission && isLisbonOrder && !checkRefundable) && !installationRefundBlocked"
                class="box-item"
                :content="installationRefundBlocked ? 'Refunds and returns for installation services require leadership approval' : lisbonRefundDisabledTooltip"
                placement="left">
              <span>
                <el-dropdown-item
                    v-if="!showRefundRequest"
                    :disabled="!hasLisbonRefundPermission || !isLisbonOrder || checkRefundable || installationRefundBlocked"
                    class="choose_action_item"
                    command="refund">
                  Refund Item(s)
                </el-dropdown-item>
              </span>
            </el-tooltip>
            <el-tooltip
                :disabled="order.order.cancellable"
                class="box-item"
                :content="order.order.cancellable_reason"
                placement="left">
              <span>
                <el-dropdown-item
                    :disabled="!order.order.cancellable"
                    class="choose_action_item action_cancel"
                    command="cancel">
                  Cancel Order
                </el-dropdown-item>
              </span>
            </el-tooltip>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
    </el-dialog>
    <ReturnDialog
        v-if="showReturnRequest"
        :visible.sync="showReturnRequest"
        :order="order"
        :total-tax="totalTax"
        :show-installation-warning="containsInstallationAsin"
        @exit-order-details="exitOrderDetails"
        @close="visible=false" />
    <ReturnOrderDetails
        v-if="showReturnDetails"
        :visible.sync="showReturnDetails"
        :order="order"
        :return-id="returnId"
        @exit-order-details="exitOrderDetails"
        @close="visible=false" />
    <FullOrderCancelDialog
        v-if="showCancelRequest & order.order.cancellable"
        :visible.sync="showCancelRequest"
        :order="order"
        @exit-cancel-order="exitCancelOrder"
        @close="visible-false" />
    <RefundDialog
        v-if="showRefundRequest"
        :visible.sync="showRefundRequest"
        :order="order"
        :total-tax="totalTax"
        :is-lisbon-order="isLisbonOrder"
        :show-installation-warning="containsInstallationAsin"
        @exit-order-details="exitOrderDetails"
        @close="visible=false" />
  </div>
</template>

<script>
import { createNamespacedHelpers as h } from "vuex";
import OrderDetailsItemView from "./OrderDetailsItemView.vue";
import OrderStatus from "./OrderStatus";
import CustomerDetailsItemView from "./CustomerDetailsItemView.vue";
import ShippingDetailsItemView from "./ShippingDetailsItemView.vue";
import numeral from "numeral";
import OrderTimeline from "./OrderTimeline.vue";
import axios from "axios";
import ShipmentDetailsNotAvailable from "./ShipmentDetailsNotAvailable";
import ShipmentDetailsAvailableSoon from "./ShipmentDetailsAvailableSoon";
import ReturnDialog from "./Return/ReturnDialog.vue";
import ReturnOrderDetails from "./Return/ReturnOrderDetails.vue";
import "../../../assets/order_details.css";
import FullOrderCancelDialog from "./Cancel/FullOrderCancelDialog.vue";
import RefundDialog from "./Refund/RefundDialog.vue";
import { hasInstallationAsin } from "@/helpers/installationAsin";

const { mapGetters: authGetters } = h("auth");
const { mapGetters: sharedGetters } = h("shared");

export default {
  name: "OrderDetails",
  components: {
    FullOrderCancelDialog,
    ReturnOrderDetails,
    ReturnDialog,
    RefundDialog,
    OrderDetailsItemView,
    OrderStatus,
    OrderTimeline,
    CustomerDetailsItemView,
    ShippingDetailsItemView,
    ShipmentDetailsNotAvailable,
    ShipmentDetailsAvailableSoon,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    orderNumber: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      currencySymbols: {
        EUR: "€",
        NONE: "",
        USD: "$",
        GBP: "£",
        AUD: "A$",
        NZD: "N$",
        CAD: "C$",
      },
      order: { order: {} },
      loading: true,
      error: null,
      showReturnRequest: false,
      shipmentsToShow: [],
      totalTax: {},
      showReturnDetails: false,
      showCancelRequest: false,
      showRefundRequest: false,
      returnId: "",
    };
  },
  computed: {
    ...authGetters(["user", "isSupervisor"]),
    ...sharedGetters(["userHasPermission"]),
    containsInstallationAsin() {
      return hasInstallationAsin(this.order.order_items);
    },
    isRestrictedRole() {
      return !this.isSupervisor;
    },
    installationRefundBlocked() {
      return this.containsInstallationAsin && this.isRestrictedRole;
    },
    isEmbedded() {
      return !!this.$route.query.orderId;
    },
    hasLisbonRefundPermission() {
      return this.userHasPermission({
        permission: "lisbon-partial-refund",
        on: "Refund",
      });
    },
    isLisbonOrder() {
      return this.orderNumber.startsWith("011");
    },
    lisbonRefundDisabledTooltip() {
      if (!this.isLisbonOrder) {
        return "Only Lisbon orders may be refunded through the partial refund flow";
      }

      if (!this.hasLisbonRefundPermission) {
        return "User role is not privileged enough to intiate a Lisbon refund";
      }

      if (this.checkRefundable) {
        return "No items in this order are currently eligible for return";
      }

      return "";
    },
    checkRefundable: function () {
      try {
        const returnableItems = (this.order.order_items || []).filter(
          (item) => item.refundable === true && item.is_soft_bundle === false
        );
        const bundles = (this.order.order_items || []).filter(
          (item) => item.is_soft_bundle === true
        );
        let index = returnableItems.length;
        let bundle_items;
        bundles.forEach((bundle) => {
          bundle_items = bundle.bundle_items.filter(
            (item) => item.refundable === true
          );
          bundle_items.forEach((item) => {
            returnableItems[index++] = item;
          });
        });
        return returnableItems.length === 0;
      } catch (e) {
        console.log("checkRefundable error", e);
        return false;
      }
    },
    checkShippedOrders: function () {
      return this.totalItemsShipped === this.order.order_items.length;
    },
    totalItemsShipped() {
      return this.order.shipments.reduce(
        (total, currentValue, _) => total + currentValue.items.length,
        0
      );
    },
  },
  mounted() {
    axios
      .get(`/v3/orders/details`, { params: { order_ids: [this.orderNumber] } })
      .then((response) => {
        if (response.data && response.data.length > 0) {
          this.order = response.data[0];
          if (this.order.order.state_name === "Cancelled") {
            for (var i = 0; i < this.order.order_items.length; i++) {
              this.order.order_items[i].status = "Cancelled";
            }
          }
          let idx = 0;
          this.order.customer.customer_name =
            this.order.customer.first_name +
            " " +
            this.order.customer.last_name;
          this.order.shipments.forEach((shipment) => {
            shipment.shipment_packages.forEach(
              (shipment_package) =>
                (this.shipmentsToShow[idx++] = shipment_package)
            );
            this.totalTax = response.data.total_tax;
          });
          for (var i = 0; i < this.order.order_items.length; i++) {
            if (
              this.order.order_items[i].quantity > 1 &&
              this.order.order_items[i].variant_title !== "bundle"
            ) {
              this.order.order_items[i] = this.expandNonBundleItems(
                this.order.order_items[i]
              );
            } else if (
              this.order.order_items[i].quantity > 1 &&
              this.order.order_items[i].variant_title === "bundle"
            ) {
              this.order.order_items[i] = this.expandBundleItems(
                this.order.order_items[i]
              );
            }
          }
        } else {
          console.log("Invalid Data");
        }
        this.loading = false;
      })
      .catch((error) => {
        console.log(error);
        this.loading = false;
        const message =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : "Something went wrong! Try again.";
        const status = error.response ? error.response.status : null;
        this.error = { message, status };
        this.$message({
          title: "Error",
          message,
          type: "error",
        });
      });
  },
  methods: {
    getValue(value) {
      this.showReturnDetails = value;
    },
    getReturnID(value) {
      this.returnId = value;
    },
    expandBundleItems(order_item) {
      let bundleItem = order_item.items;
      for (let item = 0; item < order_item.quantity - 1; item++) {
        order_item.items = order_item.items.concat(bundleItem);
      }
      return order_item;
    },
    expandNonBundleItems(order_item) {
      const itemObj = {
        id: order_item.id,
        images: order_item.images,
        sku: order_item.sku,
        title: order_item.title,
        variant_title: order_item.variant_title,
        status: order_item.status,
      };
      order_item.items = [];
      for (let item = 0; item < order_item.quantity; item++) {
        order_item.items[item] = itemObj;
      }
      return order_item;
    },
    exitOrderDetails() {
      this.visible = false;
    },
    exitCancelOrder() {
      this.visible = false;
    },
    formattedAmount(amount, currency) {
      var neg = false;
      if (amount < 0) {
        neg = true;
        amount = -amount;
      }

      amount = numeral(amount).format(`0,0.00`);
      return `${neg ? "– " : ""}${
        this.currencySymbols[currency || "USD"]
      }${amount}`;
    },
    formattedDiscountCode(type, value) {
      if (type === "percentage") {
        return `${value}%`;
      } else {
        return this.formattedAmount(
          value,
          this.order.order.total_discounts.currency_code
        );
      }
    },
    handleAction(command) {
      if (command === "return") {
        this.$emit("change-active", command);
        this.showReturnRequest = true;
        this.loading = true;
        axios
          .post("v3/returns/create_return", {
            order_id: this.order.order.displayable_order_id,
          })
          .then((response) => {
            this.$message({
              type: "success",
              message: `Return for the order #${this.order.order.displayable_order_id} has been initiated successfully`,
            });
          })
          .catch((error) => {
            console.log(error);
            this.$message({
              type: "error",
              message: `Create return has failed`,
            });
          })
          .finally(() => {
            this.loading = false;
          });
      }
      if (command === "cancel") {
        this.showCancelRequest = true;
      }
      if (command === "refund") {
        this.showRefundRequest = true;
      }
    },
  },
};
</script>

<style scoped>
.order-details-body.embedded {
  top: 0;
}
.order-details-error {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  padding: 40px;
}
.order-details-error-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
</style>
